import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { formatINR } from "@/lib/format";

const compactINR = (n) => {
  if (!isFinite(n)) return "0";
  if (Math.abs(n) >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (Math.abs(n) >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  if (Math.abs(n) >= 1e3) return `₹${(n / 1e3).toFixed(1)} K`;
  return `₹${n}`;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="border border-slate-200 bg-white shadow-md px-4 py-3">
      <p className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-2">
        {label}
      </p>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-3 text-sm">
          <span
            className="inline-block w-2.5 h-2.5"
            style={{ background: p.color }}
          />
          <span className="text-slate-700">{p.name}</span>
          <span className="font-mono-num ml-auto text-slate-900">
            {formatINR(p.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

export const ComparisonChart = ({ summary }) => {
  const s1 = summary.scenario1;
  const s2 = summary.scenario2;

  const data = [
    {
      scenario: "Below 11×",
      "Fixed Pay": s2.fixed_annual,
      Incentive: 0,
      "Variable @15×": 0,
      _label: "Below Trend",
    },
    {
      scenario: "@ 11× Target",
      "Fixed Pay": s2.fixed_annual,
      Incentive: s2.incentive_11x_annual,
      "Variable @15×": 0,
    },
    {
      scenario: "@ 15× (S1 Model)",
      "Fixed Pay": s1.fixed_annual,
      Incentive: s1.incentive_annual,
      "Variable @15×": 0,
    },
    {
      scenario: "@ 15× (S2 Model)",
      "Fixed Pay": s2.fixed_annual,
      Incentive: s2.incentive_11x_annual,
      "Variable @15×": s2.variable_annual,
    },
  ];

  return (
    <section
      className="border border-slate-200 bg-white p-8 print-block"
      data-testid="comparison-chart-section"
    >
      <div className="flex items-start justify-between mb-6">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-slate-500">
            Payout Comparison
          </p>
          <h3 className="font-display text-2xl mt-1 text-slate-900">
            Scenario 1 vs Scenario 2 — Earnings Waterfall
          </h3>
        </div>
        <div className="flex gap-4 text-xs text-slate-600">
          <LegendDot color="#1E3A8A" label="Fixed Pay" />
          <LegendDot color="#059669" label="Incentive" />
          <LegendDot color="#F59E0B" label="Variable @ 15×" />
        </div>
      </div>

      <div style={{ width: "100%", height: 340 }}>
        <ResponsiveContainer>
          <BarChart data={data} margin={{ top: 10, right: 20, left: 20, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis
              dataKey="scenario"
              tick={{ fill: "#64748B", fontSize: 12 }}
              tickLine={false}
              axisLine={{ stroke: "#CBD5E1" }}
            />
            <YAxis
              tick={{ fill: "#64748B", fontSize: 11 }}
              tickFormatter={compactINR}
              tickLine={false}
              axisLine={{ stroke: "#CBD5E1" }}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(30,58,138,0.04)" }} />
            <Bar dataKey="Fixed Pay" stackId="a" fill="#1E3A8A" />
            <Bar dataKey="Incentive" stackId="a" fill="#059669" />
            <Bar dataKey="Variable @15×" stackId="a" fill="#F59E0B" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
};

const LegendDot = ({ color, label }) => (
  <div className="flex items-center gap-2">
    <span className="inline-block w-3 h-3" style={{ background: color }} />
    <span>{label}</span>
  </div>
);

export default ComparisonChart;
